import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useFiscalYear } from '../../context/FiscalYearContext';
import { useLanguage } from '../../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, Flag, RefreshCw, Sparkles } from 'lucide-react';
import { displayFyName } from '../../utils/fiscalYearDisplay';

// FEAT-02 : % SBTi de référence (numériques) — base du "recommandé" + pré-remplissage
// du mode avancé. Miroir des constantes BE SBTI_TARGETS (objectives.py).
const SBTI_REF = {
  2030: { s12: 42, s3: 25 },
  2035: { s12: 65, s3: 39 },
};

const ObjectiveModal = ({
  show, onClose,
  objectiveForm, setObjectiveForm,
  objectiveLoading, onSubmit,
  referenceFyHasExtraordinary
}) => {
  const { isDark } = useTheme();
  const { fiscalYears } = useFiscalYear();
  const { t, language } = useLanguage();

  // FEAT-02 mode avancé (Forme A). Défaut OFF → chemin SBTi RIGOUREUSEMENT
  // inchangé (POST sans custom_*). Les % saisis sont des réductions positives
  // [0,100] (cohérent BE) ; un % plus BAS que le SBTi = MOINS ambitieux.
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customS12, setCustomS12] = useState('');
  const [customS3, setCustomS3] = useState('');

  const sbtiRef = SBTI_REF[objectiveForm.target_year] || SBTI_REF[2030];

  const openAdvanced = () => {
    if (customS12 === '') setCustomS12(sbtiRef.s12);
    if (customS3 === '') setCustomS3(sbtiRef.s3);
    setShowAdvanced(true);
  };

  // Sélection d'horizon : setObjectiveForm (1 appel, invariant test) + si avancé,
  // réaligne les customs sur le SBTi du nouvel horizon (cohérence "recommandé").
  const selectHorizon = (year) => {
    setObjectiveForm({ ...objectiveForm, target_year: year });
    if (showAdvanced) {
      setCustomS12(SBTI_REF[year].s12);
      setCustomS3(SBTI_REF[year].s3);
    }
  };

  // Payload custom : {} si mode avancé jamais activé (chemin SBTi) ; sinon les %
  // renseignés (custom partiel autorisé, l'autre retombe sur SBTi côté BE).
  const buildCustomTargets = () => {
    if (!showAdvanced) return {};
    const out = {};
    if (customS12 !== '' && customS12 !== null) out.custom_reduction_scope1_2_percent = Number(customS12);
    if (customS3 !== '' && customS3 !== null) out.custom_reduction_scope3_percent = Number(customS3);
    return out;
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center overflow-y-auto z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
            className={`w-full max-w-lg rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto my-auto ${isDark ? 'bg-slate-800' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`p-6 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-green-500/20">
                  <Target className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {t('dashboard.objectives.modalTitle')}
                  </h2>
                  <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    Science Based Targets initiative
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  {t('dashboard.objectives.referenceYearBaseline')}
                </label>
                <select
                  value={objectiveForm.reference_fiscal_year_id}
                  onChange={(e) => setObjectiveForm({ ...objectiveForm, reference_fiscal_year_id: e.target.value })}
                  className={`w-full px-4 py-3 rounded-xl border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'}`}
                >
                  <option value="">{t('dashboard.objectives.selectExercise')}</option>
                  {fiscalYears.filter(fy => fy.type !== 'scenario').map(fy => (
                    <option key={fy.id} value={fy.id}>{displayFyName(fy.name, language)}</option>
                  ))}
                </select>
                <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  {t('dashboard.objectives.baselineDesc')}
                </p>
              </div>

              {/* FEAT-01h — Commande exclure les événements extraordinaires (Q_FEAT.38 B). */}
              {/* Affichage conditionnel (Q_FEAT.39 A) : visible uniquement si le FY de */}
              {/* référence contient au moins 1 activité extraordinaire. Toggle baseline + */}
              {/* trajectory (recalcul immédiat via PUT en édition). RF1 : calcul objectif */}
              {/* local, bilan GHG officiel intact. */}
              {referenceFyHasExtraordinary && (
                <div data-testid="objective-exclude-extraordinary">
                  <label
                    className="flex items-start gap-2.5 cursor-pointer"
                    title={t('dashboard.objectives.recalculateTooltip')}
                  >
                    <input
                      type="checkbox"
                      checked={objectiveForm.exclude_extraordinary || false}
                      onChange={(e) => setObjectiveForm({ ...objectiveForm, exclude_extraordinary: e.target.checked })}
                      data-testid="objective-exclude-extraordinary-checkbox"
                      className="mt-0.5 w-4 h-4 rounded border-gray-300 text-green-500 focus:ring-green-500"
                    />
                    <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                      {t('dashboard.objectives.excludeExtraordinary')}
                      <span className={`block text-xs mt-0.5 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                        {t('dashboard.objectives.excludeExtraordinaryDesc')}
                      </span>
                    </span>
                  </label>
                </div>
              )}

              {/* FIX #1 Temps B — radio market/location pour la méthode reporting Scope 2 portée par l'objectif. */}
              <div data-testid="objective-reporting-view">
                <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  {t('dashboard.objectives.reportingView.label')}
                </label>
                <div className="grid grid-cols-1 gap-2">
                  {['market', 'location'].map(view => (
                    <label
                      key={view}
                      className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                        objectiveForm.reporting_view === view
                          ? 'border-green-500 bg-green-500/10'
                          : isDark ? 'border-slate-600 hover:border-slate-500' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="reporting_view"
                        value={view}
                        checked={objectiveForm.reporting_view === view}
                        onChange={(e) => setObjectiveForm({ ...objectiveForm, reporting_view: e.target.value })}
                        data-testid={`objective-reporting-view-${view}`}
                        className="mt-1 w-4 h-4 text-green-500 focus:ring-green-500"
                      />
                      <div>
                        <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {t(`dashboard.objectives.reportingView.${view}`)}
                        </div>
                        <div className={`text-xs mt-0.5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                          {t(`dashboard.objectives.reportingView.${view}Desc`)}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  {t('dashboard.objectives.horizonNearTerm')}
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { year: 2030, s12: '-42%', s3: '-25%' },
                    { year: 2035, s12: '-65%', s3: '-39%' }
                  ].map(opt => (
                    <button
                      key={opt.year}
                      type="button"
                      onClick={() => selectHorizon(opt.year)}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        objectiveForm.target_year === opt.year
                          ? 'border-green-500 bg-green-500/10'
                          : isDark ? 'border-slate-600 hover:border-slate-500' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className={`text-2xl font-bold mb-1 ${objectiveForm.target_year === opt.year ? 'text-green-500' : isDark ? 'text-white' : 'text-gray-900'}`}>
                        {opt.year}
                      </div>
                      <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        <span className="text-blue-500 font-medium">{opt.s12}</span> {t('dashboard.objectives.scope12')}
                      </div>
                      <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        <span className="text-amber-500 font-medium">{opt.s3}</span> {t('dashboard.objectives.scope3')}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* FEAT-02 — Forme A : lien discret + champs % éditables (mode avancé). */}
              <div data-testid="objective-advanced">
                {!showAdvanced ? (
                  <button
                    type="button"
                    onClick={openAdvanced}
                    data-testid="objective-advanced-toggle"
                    className={`text-xs underline ${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    {t('dashboard.objectives.advancedToggle')}
                  </button>
                ) : (
                  <div className="space-y-3">
                    {[
                      { key: 's12', val: customS12, set: setCustomS12, ref: sbtiRef.s12, label: t('dashboard.objectives.customScope12Label') },
                      { key: 's3', val: customS3, set: setCustomS3, ref: sbtiRef.s3, label: t('dashboard.objectives.customScope3Label') },
                    ].map(f => {
                      const lessAmbitious = f.val !== '' && Number(f.val) < f.ref;
                      return (
                        <div key={f.key}>
                          <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                            {f.label}
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={f.val}
                              onChange={(e) => f.set(e.target.value)}
                              data-testid={`objective-custom-${f.key}`}
                              className={`w-24 px-3 py-2 rounded-lg border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'}`}
                            />
                            <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                              {t('dashboard.objectives.recommended').replace('{value}', f.ref)}
                            </span>
                          </div>
                          {lessAmbitious && (
                            <p className="text-xs mt-1 text-amber-500" data-testid={`objective-less-ambitious-${f.key}`}>
                              {t('dashboard.objectives.lessAmbitiousWarning')}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-700/50' : 'bg-blue-50'}`}>
                <div className="flex items-start gap-3">
                  <Sparkles className={`w-5 h-5 mt-0.5 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />
                  <div className={`text-sm ${isDark ? 'text-slate-300' : 'text-blue-700'}`}>
                    <strong>{t('dashboard.objectives.sbtiNearTerm')}</strong>
                    <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-blue-600'}`}>
                      {t('dashboard.objectives.sbtiAlignedDesc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className={`p-6 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'} flex gap-3`}>
              <button
                onClick={onClose}
                className={`flex-1 px-4 py-3 rounded-xl border ${isDark ? 'border-slate-600 hover:bg-slate-700' : 'border-gray-200 hover:bg-gray-50'}`}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => onSubmit(buildCustomTargets())}
                disabled={!objectiveForm.reference_fiscal_year_id || objectiveLoading}
                className="flex-1 px-4 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {objectiveLoading ? (
                  <><RefreshCw className="w-5 h-5 animate-spin" />{t('common.creating')}</>
                ) : (
                  <><Flag className="w-5 h-5" />{t('dashboard.objectives.defineObjective')}</>
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ObjectiveModal;
